import { TypedEventEmitter } from "@/lib/utils";
import SimplePeer from "simple-peer";

/**
 * Interface for join request objects that will be used by signaling providers
 */
export interface JoinRequest {
  id: string;
  created: string;
  updated: string;
  requestee: string;
  room: string;
  offer: SimplePeer.SignalData | null;
  answer: SimplePeer.SignalData | null;
  step: "request" | "offer" | "answer";
}

/**
 * Generic interface for signaling providers
 * Signaling providers handle the WebRTC signaling process using different backend services
 */
export abstract class SignalingProvider extends TypedEventEmitter<{
  joinRequest: JoinRequest;
  offerUpdate: JoinRequest;
  answerUpdate: JoinRequest;
}> {
  /**
   * Current user's ID in the signaling service
   */
  abstract get userId(): string;

  /**
   * Create a new room on the signaling service
   * @returns Room ID for the created room
   */
  abstract createRoom(): Promise<string>;

  /**
   * Clean up any previous rooms owned by the current user
   */
  abstract cleanupPreviousRooms(): Promise<void>;

  /**
   * Create a join request for a room
   * @param roomId ID of the room to join
   * @returns The created join request
   */
  abstract createJoinRequest(roomId: string): Promise<JoinRequest>;

  /**
   * Update a join request with an offer
   * @param requestId ID of the join request to update
   * @param offer WebRTC offer signal data
   */
  abstract updateRequestWithOffer(requestId: string, offer: SimplePeer.SignalData): Promise<void>;

  /**
   * Update a join request with an answer
   * @param requestId ID of the join request to update
   * @param answer WebRTC answer signal data
   */
  abstract updateRequestWithAnswer(requestId: string, answer: SimplePeer.SignalData): Promise<void>;

  /**
   * Subscribe to join requests for a room
   * @param roomId ID of the room to subscribe to
   * @returns Cleanup function to unsubscribe
   */
  abstract subscribeToJoinRequests(roomId: string): Promise<() => Promise<void>>;

  /**
   * Subscribe to updates for a specific join request
   * @param requestId ID of the join request to subscribe to
   * @returns Cleanup function to unsubscribe
   */
  abstract subscribeToJoinRequestUpdates(requestId: string): Promise<() => Promise<void>>;

  /**
   * Clean up resources used by this provider
   */
  abstract cleanup(): Promise<void>;

  /**
   * Verify if a room exists
   * @param roomId ID of the room to check
   * @returns Whether the room exists
   */
  abstract roomExists(roomId: string): Promise<boolean>;

  /**
   * Delete a join request
   * @param requestId ID of the request to delete
   */
  abstract deleteJoinRequest(requestId: string): Promise<void>;
}